import { useState, ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Package,
  ShoppingBag,
  Users,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Home,
  BarChart3,
  LayoutDashboard,
  Shield,
  TrendingUp,
  Heart,
  Palette,
  Crown,
  ImageIcon,
  Layers,
  Inbox,
  Mail,
  MessageSquareQuote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { NotificationBadge } from "@/components/ui/notification-badge";
import { UserDropdown } from "@/components/ui/user-dropdown";
import { CommandPalette } from "@/components/ui/command-palette";
import { useCommandPalette } from "@/hooks/useCommandPalette";
import { BookmarkIndicator } from "@/components/ui/bookmark-indicator";

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  badge?: number;
  badgeVariant?: "default" | "success" | "warning" | "danger";
}

const navigation: NavItem[] = [
  {
    title: "Dashboard",
    href: "/admin",
    icon: LayoutDashboard,
    description: "Panel principal y métricas",
  },
  {
    title: "Productos",
    href: "/admin/products",
    icon: Palette,
    description: "Agendas y papelería artística",
  },
  {
    title: "Estampados y Hojas",
    href: "/admin/estampados-y-hojas",
    icon: ImageIcon,
    description: "Portadas y contenido de productos personalizables",
  },
  {
    title: "Secciones CEO Planner",
    href: "/admin/secciones-agenda",
    icon: Layers,
    description: "Secciones y variantes del CEO Planner personalizable",
  },
  {
    title: "Talleres",
    href: "/admin/talleres",
    icon: Users,
    description: "Inscritos en talleres",
  },
  {
    title: "Órdenes",
    href: "/admin/orders",
    icon: Heart,
    description: "Pedidos con amor y cuidado",
  },
  {
    title: "Cotizaciones",
    href: "/admin/quotes",
    icon: Inbox,
    description: "Solicitudes B2B y su seguimiento",
  },
  {
    title: "Leads",
    href: "/admin/leads",
    icon: Mail,
    description: "Emails del catálogo y del newsletter",
  },
  {
    title: "Testimonios",
    href: "/admin/testimonios",
    icon: MessageSquareQuote,
    description: "Testimonios recibidos en la postventa",
  },
  {
    title: "Usuarios",
    href: "/admin/users",
    icon: Crown,
    description: "CEOs de sus propias vidas",
  },
  /* {
    title: 'Analytics',
    href: '/admin/reports',
    icon: TrendingUp,
    description: 'Crecimiento y transformación'
  }, */
  {
    title: "Configuración",
    href: "/admin/settings",
    icon: Settings,
    description: "Ajustes del sistema",
  },
];

const Breadcrumb = () => {
  const location = useLocation();
  const pathSegments = location.pathname
    .split("/")
    .filter((segment) => segment);

  const breadcrumbItems = pathSegments.map((segment, index) => {
    const path = "/" + pathSegments.slice(0, index + 1).join("/");
    const isLast = index === pathSegments.length - 1;

    // Capitalizar y formatear el nombre
    const name =
      segment === "admin"
        ? "Admin"
        : segment.charAt(0).toUpperCase() + segment.slice(1);

    return {
      name,
      path,
      isLast,
    };
  });

  return (
    <nav className="flex items-center space-x-1 text-sm text-muted-foreground">
      <Link
        to="/"
        className="flex items-center hover:text-foreground transition-colors"
      >
        <Home className="h-4 w-4" />
        <span className="sr-only">Inicio</span>
      </Link>

      {breadcrumbItems.map((item, index) => (
        <div key={item.path} className="flex items-center">
          <ChevronRight className="h-4 w-4 mx-1" />
          {item.isLast ? (
            <span className="font-medium text-foreground">{item.name}</span>
          ) : (
            <Link
              to={item.path}
              className="hover:text-foreground transition-colors"
            >
              {item.name}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
};

const Sidebar = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-admin-sidebar border-r border-admin-border transform transition-transform duration-300 ease-in-out z-50 shadow-2xl",
          "lg:translate-x-0 lg:static lg:z-auto",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header del sidebar */}
          <div className="flex items-center justify-between p-4 border-b border-admin-text/20">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-admin-text/10 rounded-2xl flex items-center justify-center shadow-lg border-2 border-admin-text/30 backdrop-blur-sm">
                <Heart className="h-8 w-8 text-admin-text" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-admin-text font-playfair tracking-wide">
                  Admin Panel
                </h2>
                <p className="text-sm text-admin-textSecondary font-medium italic">
                  Vivi con Amor y Arte
                </p>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="lg:hidden text-admin-text hover:text-admin-text/80 transition-all duration-200"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navegación */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive =
                location.pathname === item.href ||
                (item.href !== "/admin" &&
                  location.pathname.startsWith(item.href));

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  className={cn(
                    "group flex items-center space-x-4 px-4 py-3 rounded-2xl text-base font-medium transition-all duration-200 relative",
                    "hover:bg-admin-sidebarLight/30",
                    "text-admin-text",
                    isActive ? "bg-admin-sidebarLight/20" : ""
                  )}
                >
                  {/* Bookmark Indicator */}
                  <BookmarkIndicator isActive={isActive} />

                  <item.icon className="h-6 w-6 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-bold text-base font-playfair tracking-wide">
                      {item.title}
                    </div>
                    <div className="text-sm hidden xl:block leading-relaxed font-medium text-admin-textSecondary opacity-75">
                      {item.description}
                    </div>
                  </div>
                  <div className="opacity-50 group-hover:opacity-100 transition-opacity duration-200">
                    <ChevronRight className="h-4 w-4 text-admin-textSecondary" />
                  </div>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const commandPalette = useCommandPalette();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Contenido principal */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Header */}
        <header className="bg-admin-header border-b-2 border-admin-border px-6 py-4 lg:px-8 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>

              <Breadcrumb />
            </div>

            <div className="flex items-center space-x-4">
              <UserDropdown user={user} onLogout={handleLogout} />
            </div>
          </div>
        </header>

        {/* Contenido */}
        <main className="flex-1 overflow-auto">
          <div className="h-full">{children}</div>
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPalette.isOpen}
        onClose={commandPalette.close}
      />
    </div>
  );
};

export default AdminLayout;
